import React from 'react';
import { Calendar, Download, RefreshCw, Plus } from 'lucide-react';

const VOLUME_PRESET_ORDER = ['1w', '2w', '3', '6', '12', '24', 'ytd', 'custom'];

export function DashboardHeader({
  onRefresh,
  onExport,
  onCreate,
  dateRange = 'Last 12 months',
  dateRangeLabel,
  ticketVolumePreset,
  onTicketVolumeChange,
  volumePresets = {},
}) {
  const showVolumeSelect =
    typeof onTicketVolumeChange === 'function' &&
    ticketVolumePreset != null &&
    Object.keys(volumePresets).length > 0;

  const displayRange = dateRangeLabel ?? dateRange;

  return (
    <div className="relative mb-8 sm:mb-10">
      {/* Control Center Background Card */}
      <div className="relative bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-gray-200/50 dark:border-gray-700/50 shadow-lg shadow-blue-500/5">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-indigo-500/5 rounded-2xl pointer-events-none"></div>

        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div className="space-y-1">
            <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 dark:from-gray-100 dark:via-gray-200 dark:to-gray-100 bg-clip-text text-transparent">
              Ticket Dashboard
            </h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 font-medium">
              Monitor your support ticket metrics
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {onCreate && (
              <button
                onClick={onCreate}
                className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
              >
                <Plus className="h-4 w-4" />
                <span>Create Ticket</span>
              </button>
            )}
            
            {showVolumeSelect ? (
              <label className="flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2.5 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-500 rounded-xl text-sm font-medium shadow-sm cursor-pointer hover:border-gray-400 dark:hover:border-slate-400 transition-colors">
                <Calendar className="h-4 w-4 text-indigo-600 dark:text-indigo-300 shrink-0 pointer-events-none" />
                <span className="sr-only">Ticket volume period</span>
                <select
                  className={[
                    'min-w-[10.5rem] flex-1 sm:flex-none rounded-lg border-0 py-1.5 pl-2 pr-8',
                    'text-sm font-medium text-gray-900 dark:text-slate-50',
                    'bg-gray-50 dark:bg-slate-900',
                    '[color-scheme:light] dark:[color-scheme:dark]',
                    'cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500/40 dark:focus:ring-indigo-400/50',
                  ].join(' ')}
                  value={ticketVolumePreset}
                  onChange={(e) => onTicketVolumeChange(e.target.value)}
                  title="Period for ticket volume chart and related stats"
                  aria-label="Ticket volume period"
                >
                  {VOLUME_PRESET_ORDER.filter((k) => volumePresets[k]).map((key) => (
                    <option
                      key={key}
                      value={key}
                      className="bg-white text-gray-900 dark:bg-slate-900 dark:text-slate-50"
                    >
                      {volumePresets[key]?.label || key}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <div
                className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-700 border border-gray-300 dark:border-gray-600 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 shadow-sm"
                title="Charts and volume period"
              >
                <Calendar className="h-4 w-4 text-indigo-600 dark:text-indigo-400 shrink-0" />
                <span>{displayRange}</span>
              </div>
            )}
            <button
              type="button"
              className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-700 border border-gray-300 dark:border-gray-600 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600 hover:border-gray-400 dark:hover:border-gray-500 transition-all duration-200 shadow-sm hover:shadow-md"
              onClick={onRefresh}
            >
              <RefreshCw className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span>Refresh</span>
            </button>
            <button
              type="button"
              className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-700 border border-gray-300 dark:border-gray-600 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600 hover:border-gray-400 dark:hover:border-gray-500 transition-all duration-200 shadow-sm hover:shadow-md"
              onClick={onExport}
            >
              <Download className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              <span>Export</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
